import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Pencil, Plus, Search, X } from 'lucide-react';
import { useInventoryStore, Item, NewItem, itemHasLocalStock } from '../../store/inventoryStore';
import { useProjectStore } from '../../store/projectStore';

interface Props {
  item?: Item | null;   // null/undefined = add mode
  onClose: () => void;
  onSaved: () => void;
}

const InventoryItemDrawer: React.FC<Props> = ({ item, onClose, onSaved }) => {
  const { items, stockUnits, addItem, updateItem, renameCategory, setItemDiscontinued, isItemInUse } = useInventoryStore();
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';
  const isAdd = !item;
  const liveItem = item ? (items.find(i => i.id === item.id) ?? item) : null;
  const isDiscontinued = liveItem?.isDiscontinued ?? false;

  // ── Animation ──────────────────────────────────────────────────────────────
  const [visible, setVisible] = useState(false);
  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);
  const close = () => { setVisible(false); setTimeout(onClose, 220); };

  // ── Form state ─────────────────────────────────────────────────────────────
  const [name, setName]         = useState(item?.name ?? '');
  const [sku, setSku]           = useState(item?.sku ?? '');
  const [category, setCategory] = useState(item?.category ?? '');
  const [notes, setNotes]       = useState(item?.notes ?? '');
  const [requiresSerial, setRequiresSerial] = useState(item?.requiresSerial ?? false);
  const [requiresMac, setRequiresMac]       = useState(item?.requiresMac ?? false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [identityLocked, setIdentityLocked] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categoryQuery, setCategoryQuery] = useState('');
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [categoryRenameBusy, setCategoryRenameBusy] = useState(false);
  const [skuTipOpen, setSkuTipOpen] = useState(false);
  const [receivingTipOpen, setReceivingTipOpen] = useState(false);
  const [nameSuggestionsOpen, setNameSuggestionsOpen] = useState(false);
  const [skuSuggestionsOpen, setSkuSuggestionsOpen] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const categorySearchRef = useRef<HTMLInputElement>(null);
  const categoryEditRef = useRef<HTMLInputElement>(null);
  const nameFieldRef = useRef<HTMLDivElement>(null);
  const skuFieldRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const skuInputRef = useRef<HTMLInputElement>(null);
  const scanBufRef = useRef({ text: '', at: 0 });

  const existingCategories = useMemo(() => {
    const set = new Set(
      items.map(i => i.category?.trim()).filter((c): c is string => !!c),
    );
    // Keep current value in the list when editing an existing/orphan category
    if (category.trim()) set.add(category.trim());
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items, category]);

  const filteredCategories = useMemo(() => {
    const q = categoryQuery.trim().toLowerCase();
    if (!q) return existingCategories;
    return existingCategories.filter(c => c.toLowerCase().includes(q));
  }, [existingCategories, categoryQuery]);

  const canCreateCategory = useMemo(() => {
    const q = categoryQuery.trim();
    if (!q) return false;
    return !existingCategories.some(c => c.toLowerCase() === q.toLowerCase());
  }, [categoryQuery, existingCategories]);

  useEffect(() => {
    if (!categoryOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
        setCategoryQuery('');
        setEditingCategory(null);
        setEditCategoryName('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [categoryOpen]);

  useEffect(() => {
    if (categoryOpen) {
      setCategoryQuery('');
      setEditingCategory(null);
      setEditCategoryName('');
      requestAnimationFrame(() => categorySearchRef.current?.focus());
    }
  }, [categoryOpen]);

  useEffect(() => {
    if (!nameSuggestionsOpen && !skuSuggestionsOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (nameSuggestionsOpen && nameFieldRef.current && !nameFieldRef.current.contains(target)) {
        setNameSuggestionsOpen(false);
      }
      if (skuSuggestionsOpen && skuFieldRef.current && !skuFieldRef.current.contains(target)) {
        setSkuSuggestionsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [nameSuggestionsOpen, skuSuggestionsOpen]);

  useEffect(() => {
    if (!item) {
      setIdentityLocked(false);
      return;
    }
    if (itemHasLocalStock(item.id, stockUnits)) {
      setIdentityLocked(true);
      return;
    }
    let cancelled = false;
    void isItemInUse(item.id).then(inUse => {
      if (!cancelled) setIdentityLocked(inUse);
    });
    return () => { cancelled = true; };
  }, [item, stockUnits, isItemInUse]);

  const pickCategory = (value: string) => {
    setCategory(value);
    setCategoryOpen(false);
    setCategoryQuery('');
    setEditingCategory(null);
    setEditCategoryName('');
  };

  const startEditCategory = (name: string) => {
    setEditingCategory(name);
    setEditCategoryName(name);
    requestAnimationFrame(() => {
      categoryEditRef.current?.focus();
      categoryEditRef.current?.select();
    });
  };

  const commitEditCategory = async () => {
    if (!editingCategory) return;
    const next = editCategoryName.trim();
    if (!next) return;
    if (next === editingCategory) {
      setEditingCategory(null);
      setEditCategoryName('');
      return;
    }
    setCategoryRenameBusy(true);
    setError('');
    const ok = await renameCategory(editingCategory, next);
    setCategoryRenameBusy(false);
    if (!ok) {
      setError(useInventoryStore.getState().error || 'Failed to rename category.');
      return;
    }
    if (category.trim() === editingCategory) setCategory(next);
    setEditingCategory(null);
    setEditCategoryName('');
  };

  const similarItems = useMemo(() => {
    const q = name.trim().toLowerCase();
    if (q.length < 1) return [];
    return items
      .filter(i => {
        if (item && i.id === item.id) return false;
        return i.name.toLowerCase().includes(q);
      })
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 8);
  }, [items, name, item]);

  const exactNameTaken = useMemo(() => {
    const q = name.trim().toLowerCase();
    if (!q) return false;
    return items.some(i => {
      if (item && i.id === item.id) return false;
      return i.name.trim().toLowerCase() === q;
    });
  }, [items, name, item]);

  const similarSkus = useMemo(() => {
    const q = sku.trim().toLowerCase();
    if (q.length < 1) return [];
    return items
      .filter(i => {
        if (item && i.id === item.id) return false;
        const s = i.sku?.trim();
        if (!s) return false;
        return s.toLowerCase().includes(q);
      })
      .sort((a, b) => (a.sku ?? '').localeCompare(b.sku ?? ''))
      .slice(0, 8);
  }, [items, sku, item]);

  const exactSkuTaken = useMemo(() => {
    const q = sku.trim().toLowerCase();
    if (!q) return false;
    return items.some(i => {
      if (item && i.id === item.id) return false;
      return (i.sku?.trim().toLowerCase() ?? '') === q;
    });
  }, [items, sku, item]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!name.trim()) { setError('Item name is required.'); return; }
    if (!identityLocked && exactNameTaken) {
      setError('An item with this name already exists. Names must be unique.');
      return;
    }
    if (!identityLocked && exactSkuTaken) {
      setError('An item with this SKU already exists. SKUs must be unique.');
      return;
    }
    setSaving(true);
    setError('');

    const payload: NewItem = {
      name: name.trim(),
      sku: sku.trim() || null,
      category: category.trim() || null,
      notes: notes.trim() || null,
      requiresSerial,
      requiresMac,
    };

    let ok = true;
    if (isAdd) {
      const created = await addItem(payload);
      ok = created != null;
    } else if (identityLocked) {
      ok = await updateItem(item!.id, {
        category: payload.category,
        notes: payload.notes,
        requiresSerial: payload.requiresSerial,
        requiresMac: payload.requiresMac,
      });
    } else {
      ok = await updateItem(item!.id, payload);
    }

    setSaving(false);
    if (!ok) {
      setError(useInventoryStore.getState().error || 'Failed to save item.');
      return;
    }
    onSaved();
    close();
  };

  const applyScannedCode = (code: string) => {
    const trimmed = code.trim();
    if (!trimmed || identityLocked) return;
    const nameEmpty = !(nameInputRef.current?.value ?? name).trim();
    const skuEmpty = !(skuInputRef.current?.value ?? sku).trim();
    const active = document.activeElement;
    if (active === skuInputRef.current || (nameEmpty === false && skuEmpty)) {
      setSku(trimmed);
      setSkuSuggestionsOpen(true);
      if (error) setError('');
      return;
    }
    if (active === nameInputRef.current || nameEmpty) {
      setName(trimmed);
      setNameSuggestionsOpen(true);
      if (error) setError('');
      requestAnimationFrame(() => skuInputRef.current?.focus());
      return;
    }
    setSku(trimmed);
    setSkuSuggestionsOpen(true);
    if (error) setError('');
  };

  const onDrawerScanKeyDown = (e: React.KeyboardEvent) => {
    if (identityLocked) return;
    const target = e.target as HTMLElement;
    const inName = target === nameInputRef.current;
    const inSku = target === skuInputRef.current;
    const tag = target.tagName;
    const inOtherField =
      !inName && !inSku && (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT');

    if (inName && e.key === 'Enter') {
      e.preventDefault();
      setNameSuggestionsOpen(false);
      skuInputRef.current?.focus();
      scanBufRef.current = { text: '', at: 0 };
      return;
    }
    if (inSku && e.key === 'Enter') {
      e.preventDefault();
      setSkuSuggestionsOpen(false);
      scanBufRef.current = { text: '', at: 0 };
      return;
    }

    const now = Date.now();
    if (now - scanBufRef.current.at > 50) scanBufRef.current.text = '';
    scanBufRef.current.at = now;

    if (e.key === 'Enter') {
      const code = scanBufRef.current.text.trim();
      scanBufRef.current.text = '';
      if (!inOtherField && code.length >= 3) {
        e.preventDefault();
        applyScannedCode(code);
      }
      return;
    }

    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && !inOtherField && !inName && !inSku) {
      scanBufRef.current.text += e.key;
    }
  };

  const handleToggleDiscontinued = async () => {
    if (!item) return;
    setStatusBusy(true);
    setError('');
    const ok = await setItemDiscontinued(item.id, !isDiscontinued);
    setStatusBusy(false);
    if (!ok) {
      setError(useInventoryStore.getState().error || 'Failed to update item status.');
      return;
    }
    onSaved();
  };

  // ── Colours ────────────────────────────────────────────────────────────────
  const drawerBg = isDark ? '#20242b' : '#ffffff';
  const border   = isDark ? '#343a43' : '#e5e7eb';
  const text     = isDark ? '#e5e7eb' : '#111827';
  const muted    = isDark ? '#9ca3af' : '#6b7280';
  const inputBg  = isDark ? '#171a1f' : '#f9fafb';
  const panelBg  = isDark ? '#1e2330' : '#ffffff';
  const rowHover = isDark ? '#262b35' : '#f3f4f6';
  const activeColor = isDark ? '#93c5fd' : '#1d4ed8';
  const labelCls = 'text-sm font-medium mb-1.5 block';
  const inputCls = 'w-full text-sm rounded px-3 py-2 border focus:outline-none focus:ring-1 focus:ring-blue-500';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 transition-opacity duration-200"
        style={{ backgroundColor: 'rgba(0,0,0,0.4)', opacity: visible ? 1 : 0 }}
        onClick={close}
      />

      {/* Drawer */}
      <div
        className="fixed top-0 right-0 h-full z-50 flex flex-col shadow-2xl"
        onKeyDown={onDrawerScanKeyDown}
        style={{
          width: 400,
          backgroundColor: drawerBg,
          borderLeft: `1px solid ${border}`,
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 220ms cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${border}` }}>
          <div>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: muted }}>
              {isAdd ? 'New Item' : 'Edit Item'}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <h2 className="text-base font-semibold" style={{ color: text }}>
                {isAdd ? 'Add Inventory Item' : item?.name}
              </h2>
              {!isAdd && isDiscontinued && (
                <span
                  className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: isDark ? 'rgba(194,65,12,0.2)' : '#ffedd5',
                    color: isDark ? '#fdba74' : '#c2410c',
                  }}
                >
                  Discontinued
                </span>
              )}
            </div>
          </div>
          <button onClick={close} className="p-1.5 rounded hover:bg-gray-700/50 transition-colors" style={{ color: muted }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="px-5 pt-4 pb-6 space-y-5">
            <p className="text-[11px] uppercase tracking-wide font-medium" style={{ color: muted }}>
              Details
            </p>
            <div className="relative" ref={nameFieldRef}>
              <label className={labelCls} style={{ color: text }}>Item Name *</label>
              <input
                ref={nameInputRef}
                value={name}
                onChange={e => {
                  if (identityLocked) return;
                  setName(e.target.value);
                  setNameSuggestionsOpen(true);
                  if (error) setError('');
                }}
                onFocus={() => {
                  if (!identityLocked) setNameSuggestionsOpen(true);
                }}
                placeholder="e.g. Litebeam AC"
                autoFocus={!identityLocked}
                autoComplete="off"
                spellCheck={false}
                readOnly={identityLocked}
                className={inputCls}
                style={{
                  backgroundColor: identityLocked ? (isDark ? '#1a1d24' : '#f3f4f6') : inputBg,
                  borderColor: (error && !name.trim()) || (!identityLocked && exactNameTaken) ? '#ef4444' : border,
                  color: text,
                  cursor: identityLocked ? 'default' : undefined,
                  opacity: identityLocked ? 0.85 : 1,
                }}
              />
              {identityLocked && (
                <p className="text-sm mt-1.5" style={{ color: muted }}>
                  Name is locked because this item has stock or request history. Create a new item type for a different product.
                </p>
              )}
              {!identityLocked && exactNameTaken && (
                <p className="text-sm mt-1.5 text-red-400">
                  This name is already used. Item names must be unique.
                </p>
              )}
              {!identityLocked && nameSuggestionsOpen && similarItems.length > 0 && (
                <div
                  className="mt-1.5 rounded-md overflow-hidden pointer-events-none"
                  style={{
                    backgroundColor: panelBg,
                    border: `1px solid ${border}`,
                  }}
                >
                  <p
                    className="px-3 py-2 text-[11px] uppercase tracking-wide font-medium"
                    style={{
                      color: muted,
                      borderBottom: `1px solid ${border}`,
                      backgroundColor: isDark ? '#1a1f28' : '#f9fafb',
                    }}
                  >
                    Similar existing items
                  </p>
                  <div className="max-h-40 overflow-y-auto">
                    {similarItems.map(i => {
                      const isExact = i.name.trim().toLowerCase() === name.trim().toLowerCase();
                      return (
                        <div
                          key={i.id}
                          className="px-3 py-2 text-sm truncate flex items-center justify-between gap-2"
                          style={{
                            color: isExact ? (isDark ? '#fca5a5' : '#b91c1c') : muted,
                            backgroundColor: isExact
                              ? (isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2')
                              : 'transparent',
                          }}
                        >
                          <span className="truncate">{i.name}</span>
                          {isExact && (
                            <span className="flex-shrink-0 text-xs font-medium uppercase tracking-wide">
                              Exact match
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={skuFieldRef}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <label className="text-sm font-medium" style={{ color: text }}>SKU</label>
                <span
                  className="relative inline-flex items-center"
                  onMouseEnter={() => setSkuTipOpen(true)}
                  onMouseLeave={() => setSkuTipOpen(false)}
                >
                  <button
                    type="button"
                    className="p-0.5 rounded transition-colors"
                    style={{ color: isDark ? '#60a5fa' : '#3b82f6' }}
                    aria-label="What SKU means"
                    tabIndex={-1}
                  >
                    <Search size={14} strokeWidth={2.5} />
                  </button>
                  {skuTipOpen && (
                    <span
                      className="absolute left-0 bottom-full mb-1.5 z-20 pointer-events-none rounded-md px-3 py-2 text-sm leading-snug whitespace-normal"
                      style={{
                        width: 220,
                        backgroundColor: isDark ? '#1e2330' : '#1f2937',
                        color: '#e5e7eb',
                        border: `1px solid ${isDark ? '#3a4155' : '#374151'}`,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
                      }}
                    >
                      Optional. If set, must be unique across item types.
                      <span
                        className="absolute left-2 top-full"
                        style={{
                          width: 0,
                          height: 0,
                          borderLeft: '5px solid transparent',
                          borderRight: '5px solid transparent',
                          borderTop: `5px solid ${isDark ? '#3a4155' : '#374151'}`,
                        }}
                      />
                    </span>
                  )}
                </span>
              </div>
              <input
                ref={skuInputRef}
                value={sku}
                onChange={e => {
                  if (identityLocked) return;
                  setSku(e.target.value);
                  setSkuSuggestionsOpen(true);
                  if (error) setError('');
                }}
                onFocus={() => {
                  if (!identityLocked) setSkuSuggestionsOpen(true);
                }}
                placeholder="e.g. LB-AC-001"
                autoComplete="off"
                spellCheck={false}
                readOnly={identityLocked}
                className={inputCls}
                style={{
                  backgroundColor: identityLocked ? (isDark ? '#1a1d24' : '#f3f4f6') : inputBg,
                  borderColor: !identityLocked && exactSkuTaken ? '#ef4444' : border,
                  color: text,
                  cursor: identityLocked ? 'default' : undefined,
                  opacity: identityLocked ? 0.85 : 1,
                }}
              />
              {identityLocked && (
                <p className="text-sm mt-1.5" style={{ color: muted }}>
                  SKU is locked for the same reason. Use a new item type for a new SKU.
                </p>
              )}
              {!identityLocked && exactSkuTaken && (
                <p className="text-sm mt-1.5 text-red-400">
                  This SKU is already used. SKUs must be unique.
                </p>
              )}
              {!identityLocked && skuSuggestionsOpen && similarSkus.length > 0 && (
                <div
                  className="mt-1.5 rounded-md overflow-hidden pointer-events-none"
                  style={{
                    backgroundColor: panelBg,
                    border: `1px solid ${border}`,
                  }}
                >
                  <p
                    className="px-3 py-2 text-[11px] uppercase tracking-wide font-medium"
                    style={{
                      color: muted,
                      borderBottom: `1px solid ${border}`,
                      backgroundColor: isDark ? '#1a1f28' : '#f9fafb',
                    }}
                  >
                    Similar existing SKUs
                  </p>
                  <div className="max-h-40 overflow-y-auto">
                    {similarSkus.map(i => {
                      const isExact = (i.sku?.trim().toLowerCase() ?? '') === sku.trim().toLowerCase();
                      return (
                        <div
                          key={i.id}
                          className="px-3 py-2 text-sm truncate flex items-center justify-between gap-2"
                          style={{
                            color: isExact ? (isDark ? '#fca5a5' : '#b91c1c') : muted,
                            backgroundColor: isExact
                              ? (isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2')
                              : 'transparent',
                          }}
                        >
                          <span className="truncate">
                            <span style={{ color: isExact ? undefined : text }}>{i.sku}</span>
                            <span style={{ color: muted }}> · {i.name}</span>
                          </span>
                          {isExact && (
                            <span className="flex-shrink-0 text-xs font-medium uppercase tracking-wide">
                              Exact match
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div ref={categoryRef} className="relative">
              <label className={labelCls} style={{ color: text }}>Category</label>
              <button
                type="button"
                onClick={() => setCategoryOpen(o => !o)}
                className={`${inputCls} flex items-center justify-between gap-2 text-left`}
                style={{
                  backgroundColor: inputBg,
                  borderColor: border,
                  color: category ? text : muted,
                }}
              >
                <span className="truncate">{category || 'Select or add category…'}</span>
                <ChevronDown
                  size={16}
                  className={`flex-shrink-0 transition-transform ${categoryOpen ? 'rotate-180' : ''}`}
                  style={{ color: muted }}
                />
              </button>

              {categoryOpen && (
                <div
                  className="absolute left-0 right-0 top-full mt-1 z-20 rounded-md shadow-xl overflow-hidden flex flex-col"
                  style={{
                    backgroundColor: panelBg,
                    border: `1px solid ${border}`,
                    maxHeight: 280,
                  }}
                >
                  <div className="p-2 flex-shrink-0" style={{ borderBottom: `1px solid ${border}` }}>
                    <input
                      ref={categorySearchRef}
                      type="text"
                      value={categoryQuery}
                      onChange={e => setCategoryQuery(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const q = categoryQuery.trim();
                          if (!q) return;
                          pickCategory(q);
                        }
                        if (e.key === 'Escape') {
                          setCategoryOpen(false);
                          setCategoryQuery('');
                        }
                      }}
                      placeholder="Search or type new…"
                      className="w-full text-sm px-3 py-2 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                      style={{ backgroundColor: inputBg, borderColor: border, color: text }}
                    />
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                    {category && (
                      <button
                        type="button"
                        onClick={() => pickCategory('')}
                        className="w-full text-left px-3 py-2 text-sm transition-colors"
                        style={{ color: muted }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = rowHover; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        Clear category
                      </button>
                    )}

                    {filteredCategories.length === 0 && !canCreateCategory && (
                      <div className="px-3 py-3 text-sm text-center" style={{ color: muted }}>
                        No categories yet. Type a name to add one.
                      </div>
                    )}

                    {filteredCategories.map(c => {
                      const selected = c.toLowerCase() === category.trim().toLowerCase();
                      const isEditing = editingCategory === c;
                      return (
                        <div
                          key={c}
                          className="flex items-center gap-0.5 pr-1"
                          style={{
                            backgroundColor: selected && !isEditing ? rowHover : 'transparent',
                          }}
                        >
                          {isEditing ? (
                            <>
                              <input
                                ref={categoryEditRef}
                                type="text"
                                value={editCategoryName}
                                onChange={e => setEditCategoryName(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    void commitEditCategory();
                                  }
                                  if (e.key === 'Escape') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingCategory(null);
                                    setEditCategoryName('');
                                  }
                                }}
                                disabled={categoryRenameBusy}
                                className="flex-1 min-w-0 text-sm px-3 py-2 border-0 focus:outline-none focus:ring-0"
                                style={{ backgroundColor: 'transparent', color: text }}
                              />
                              <button
                                type="button"
                                onClick={() => void commitEditCategory()}
                                disabled={!editCategoryName.trim() || categoryRenameBusy}
                                className="p-1 rounded transition-colors disabled:opacity-40"
                                title="Save category name"
                                style={{ color: '#16a34a' }}
                              >
                                <Check size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingCategory(null);
                                  setEditCategoryName('');
                                }}
                                disabled={categoryRenameBusy}
                                className="p-1 rounded transition-colors"
                                title="Cancel"
                                style={{ color: muted }}
                              >
                                <X size={14} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => pickCategory(c)}
                                className="flex-1 min-w-0 text-left px-3 py-2 text-sm truncate transition-colors"
                                style={{ color: selected ? activeColor : text }}
                                onMouseEnter={e => { e.currentTarget.parentElement!.style.backgroundColor = rowHover; }}
                                onMouseLeave={e => {
                                  e.currentTarget.parentElement!.style.backgroundColor = selected ? rowHover : 'transparent';
                                }}
                              >
                                {c}
                              </button>
                              <button
                                type="button"
                                onClick={() => startEditCategory(c)}
                                className="p-1 rounded transition-colors opacity-70 hover:opacity-100"
                                title="Rename category"
                                style={{ color: muted }}
                                onMouseEnter={e => { e.currentTarget.parentElement!.style.backgroundColor = rowHover; }}
                                onMouseLeave={e => {
                                  e.currentTarget.parentElement!.style.backgroundColor = selected ? rowHover : 'transparent';
                                }}
                              >
                                <Pencil size={13} />
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {canCreateCategory && (
                    <button
                      type="button"
                      onClick={() => pickCategory(categoryQuery.trim())}
                      className="w-full text-left px-3 py-2.5 text-sm font-medium flex items-center gap-1.5 flex-shrink-0 transition-colors"
                      style={{
                        color: activeColor,
                        borderTop: `1px solid ${border}`,
                        backgroundColor: isDark ? '#1a2540' : '#eff6ff',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = isDark ? '#1e2a4a' : '#dbeafe';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = isDark ? '#1a2540' : '#eff6ff';
                      }}
                    >
                      <Plus size={14} />
                      Add “{categoryQuery.trim()}”
                    </button>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className={labelCls} style={{ color: text }}>Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                placeholder="Optional notes…"
                className={`${inputCls} resize-none`}
                style={{ backgroundColor: inputBg, borderColor: border, color: text }}
              />
            </div>

            <div>
              <div className="relative flex items-center gap-1.5 mb-2">
                <label className="text-sm font-medium" style={{ color: text }}>
                  Receiving requirements
                </label>
                <span
                  className="inline-flex items-center"
                  onMouseEnter={() => setReceivingTipOpen(true)}
                  onMouseLeave={() => setReceivingTipOpen(false)}
                >
                  <button
                    type="button"
                    className="p-0.5 rounded transition-colors"
                    style={{ color: isDark ? '#60a5fa' : '#3b82f6' }}
                    aria-label="What receiving requirements mean"
                    tabIndex={-1}
                  >
                    <Search size={14} strokeWidth={2.5} />
                  </button>
                </span>
                {receivingTipOpen && (
                  <span
                    className="absolute left-0 right-0 bottom-full mb-1.5 z-20 pointer-events-none rounded-md px-3 py-2 text-sm leading-snug"
                    style={{
                      backgroundColor: isDark ? '#1e2330' : '#1f2937',
                      color: '#e5e7eb',
                      border: `1px solid ${isDark ? '#3a4155' : '#374151'}`,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
                    }}
                  >
                    When enabled, serials or MACs must be entered before stock can be received into inventory.
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2.5">
                <label className="flex items-center gap-2.5 text-sm cursor-pointer" style={{ color: text }}>
                  <input
                    type="checkbox"
                    checked={requiresSerial}
                    onChange={e => setRequiresSerial(e.target.checked)}
                    className="rounded w-4 h-4"
                    style={{
                      colorScheme: isDark ? 'dark' : 'light',
                      accentColor: '#3b82f6',
                    }}
                  />
                  Requires Serial Number
                </label>
                <label className="flex items-center gap-2.5 text-sm cursor-pointer" style={{ color: text }}>
                  <input
                    type="checkbox"
                    checked={requiresMac}
                    onChange={e => setRequiresMac(e.target.checked)}
                    className="rounded w-4 h-4"
                    style={{
                      colorScheme: isDark ? 'dark' : 'light',
                      accentColor: '#3b82f6',
                    }}
                  />
                  Requires MAC Address
                </label>
              </div>
            </div>

            {error && <p className="text-sm text-red-400">{error}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 flex-shrink-0" style={{ borderTop: `1px solid ${border}` }}>
          <div>
            {!isAdd && (
              <button
                type="button"
                onClick={handleToggleDiscontinued}
                disabled={statusBusy || saving}
                className="px-3 py-2 text-sm rounded border transition-colors disabled:opacity-50"
                style={{
                  borderColor: isDiscontinued ? (isDark ? '#14532d' : '#bbf7d0') : (isDark ? '#7c2d12' : '#fed7aa'),
                  color: isDiscontinued ? (isDark ? '#4ade80' : '#15803d') : (isDark ? '#fdba74' : '#c2410c'),
                  backgroundColor: isDiscontinued
                    ? (isDark ? '#14532d30' : '#f0fdf4')
                    : (isDark ? '#7c2d1230' : '#fff7ed'),
                }}
              >
                {statusBusy
                  ? 'Updating…'
                  : isDiscontinued
                    ? 'Reactivate'
                    : 'Discontinue'}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={close}
              className="px-3 py-2 text-sm rounded border transition-colors hover:bg-gray-700/30"
              style={{ borderColor: border, color: muted }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || statusBusy || (!identityLocked && (exactNameTaken || exactSkuTaken))}
              className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : isAdd ? 'Add Item' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default InventoryItemDrawer;
